import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getNodeByKey,
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  LexicalNode,
} from 'lexical';
import { useEffect } from 'react';

import {
  $unwrapInlineStyleNode,
  $wrapSelectionInInlineStyleNode,
} from '../inlineStyle';
import {
  APPLY_HIGHLIGHT_COMMAND,
  REMOVE_HIGHLIGHT_COMMAND,
} from './highlightCommands';
import { HighlightColor } from './highlightConstants';
import {
  $createHighlightNode,
  $isHighlightNode,
  HighlightNode,
} from './HighlightNode';

/**
 * Walk up from the given node to find the nearest HighlightNode ancestor.
 */
export function $findHighlightNode(
  node: LexicalNode | null
): HighlightNode | null {
  let current = node;
  while (current !== null) {
    if ($isHighlightNode(current)) {
      return current;
    }
    current = current.getParent();
  }
  return null;
}

/**
 * Unwrap highlight nodes that overlap the given extracted nodes while
 * preserving the highlight on children that fall outside the extracted set.
 *
 * Call `selection.extract()` before this to split text at selection boundaries,
 * then pass the resulting nodes here.
 */
function $unwrapHighlightsPreservingOutside(
  extractedNodes: LexicalNode[]
): void {
  const extractedKeys = new Set(extractedNodes.map((n) => n.getKey()));

  // Collect children of each affected highlight that are NOT in the extracted
  // (selected) set — these need to be re-wrapped with their original color.
  const preserveGroups: Array<{ keys: string[]; color: HighlightColor }> = [];
  const seenHighlights = new Set<string>();

  for (const node of extractedNodes) {
    const highlight = $findHighlightNode(node);
    if (!highlight || seenHighlights.has(highlight.getKey())) continue;
    seenHighlights.add(highlight.getKey());

    const hlColor = highlight.getColor();
    const toPreserve: string[] = [];
    for (const child of highlight.getChildren()) {
      if (!extractedKeys.has(child.getKey())) {
        toPreserve.push(child.getKey());
      }
    }
    if (toPreserve.length > 0) {
      preserveGroups.push({ keys: toPreserve, color: hlColor });
    }
  }

  // Unwrap all highlights that overlap the extracted nodes.
  for (const node of extractedNodes) {
    const highlight = $findHighlightNode(node);
    if (highlight) {
      $unwrapInlineStyleNode(highlight);
    }
  }

  // Re-wrap preserved nodes with their original highlight colors, grouping
  // contiguous siblings into a single HighlightNode.
  for (const { keys, color: originalColor } of preserveGroups) {
    let currentHighlight: HighlightNode | null = null;
    for (const key of keys) {
      const node = $getNodeByKey(key);
      if (!node || $findHighlightNode(node)) {
        currentHighlight = null;
        continue;
      }

      if (
        currentHighlight !== null &&
        node.getPreviousSibling()?.is(currentHighlight)
      ) {
        currentHighlight.append(node);
      } else {
        currentHighlight = $createHighlightNode(originalColor);
        node.insertBefore(currentHighlight);
        currentHighlight.append(node);
      }
    }
  }
}

function $removeHighlightFromSelection(): void {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  // Collapsed caret: there's no range to extract, so operate on the whole
  // ancestor highlight directly. Mirrors $applyHighlight's collapsed-caret
  // branch so that clicking "None" and clicking a color with no selection
  // both affect the entire highlight rather than a single text child.
  if (selection.isCollapsed()) {
    const anchorHighlight = $findHighlightNode(selection.anchor.getNode());
    if (anchorHighlight) {
      $unwrapInlineStyleNode(anchorHighlight);
    }
    return;
  }

  const extractedNodes = selection.extract();
  $unwrapHighlightsPreservingOutside(extractedNodes);
}

function $applyHighlight(color: HighlightColor): void {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) return;

  const anchorHighlight = $findHighlightNode(selection.anchor.getNode());
  const focusHighlight = $findHighlightNode(selection.focus.getNode());

  // Collapsed caret: we can't wrap an empty range, so instead act on the
  // ancestor highlight if the caret sits inside one. Without this, clicking a
  // color button while the caret is inside an existing highlight would
  // silently do nothing — while "None" would still work via
  // REMOVE_HIGHLIGHT_COMMAND, creating an inconsistent UX.
  if (selection.isCollapsed()) {
    if (anchorHighlight) {
      if (anchorHighlight.getColor() === color) {
        $unwrapInlineStyleNode(anchorHighlight);
      } else {
        anchorHighlight.setColor(color);
      }
    }
    return;
  }

  // Split text at selection boundaries so selected vs non-selected portions
  // within a highlight become separate nodes.
  const extractedNodes = selection.extract();

  // If the selection sits inside a single highlight of the same color, treat
  // it as a toggle: unwrap only the selected portion, leaving any text
  // outside the selection still highlighted.
  if (
    anchorHighlight &&
    focusHighlight &&
    anchorHighlight.is(focusHighlight) &&
    anchorHighlight.getColor() === color
  ) {
    $unwrapHighlightsPreservingOutside(extractedNodes);
    return;
  }

  // Unwrap highlights in the selection while preserving outside portions.
  $unwrapHighlightsPreservingOutside(extractedNodes);

  // Re-read selection after unwrapping — node references may have changed.
  const freshSelection = $getSelection();
  if (!$isRangeSelection(freshSelection)) return;

  $wrapSelectionInInlineStyleNode(
    freshSelection,
    freshSelection.isBackward(),
    () => $createHighlightNode(color),
    $isHighlightNode
  );
}

export function HighlightPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const removeApply = editor.registerCommand(
      APPLY_HIGHLIGHT_COMMAND,
      (color) => {
        editor.update(() => {
          $applyHighlight(color);
        });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    const removeRemove = editor.registerCommand(
      REMOVE_HIGHLIGHT_COMMAND,
      () => {
        editor.update(() => {
          $removeHighlightFromSelection();
        });
        return true;
      },
      COMMAND_PRIORITY_LOW
    );

    return () => {
      removeApply();
      removeRemove();
    };
  }, [editor]);

  return null;
}
